import type {Dispatch, RefObject} from "react";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {create} from "zustand";
import {subscribeWithSelector} from "zustand/middleware";
import {setupDragHandlers} from "./use-drag";

type Point = {x: number; y: number};

type Key = number | string;

type DragInfo = {
  key: Key;
  pos: Point;
  startIndex: number;
  currentIndex: number | null;
  gapRect: DOMRect;
  onDrop: (() => void) | null;
};

type DropAnimation = {
  from: Point;
  key: Key;
  timeoutId: ReturnType<typeof setTimeout>;
};

export const useDragStore = create(
  subscribeWithSelector<{
    dragInfo: null | DragInfo;
    setDragInfo: (dragInfo: DragInfo | null) => void;
    updateDragInfo: (dragInfo: Partial<DragInfo>) => void;

    dropAnimation: null | DropAnimation;
    setDropAnimation: (dropAnimation: DropAnimation | null) => void;
  }>((set) => ({
    dragInfo: null,
    setDragInfo: (dragInfo) => set({dragInfo}),
    updateDragInfo: (dragInfo) => set((prev) => ({dragInfo: {...prev.dragInfo!, ...dragInfo}})),

    dropAnimation: null,
    setDropAnimation: (dropAnimation) => set({dropAnimation}),
  }))
);

type MoveStyleManager = {
  resetTo(pos: Point): void;
  setNextPosition(pos: Point): void;
  reapply(): void;
};

const createMoveStyleManager = (opts: {
  node: HTMLElement;
  nodeRect: DOMRect;
  startPos: Point;
  key: Key;
  // the transform the node rests at once released. Read at release time, since the list may have
  // changed during the drag, so the transform from drag start can be stale.
  getRestTransform: () => string;
}): MoveStyleManager => {
  const {node, nodeRect, startPos, key, getRestTransform} = opts;
  let lastPos = startPos;
  const prevStyles = {
    position: node.style.position,
    width: node.style.width,
    height: node.style.height,
    transform: node.style.transform,
    zIndex: node.style.zIndex,
  };
  node.style.position = "fixed";
  node.style.width = `${nodeRect.width}px`;
  node.style.height = `${nodeRect.height}px`;
  node.style.transform = `translate(${nodeRect.left}px, ${nodeRect.top}px)`;
  node.style.zIndex = "1";

  return {
    resetTo: () => {
      const preReleaseRect = node.getBoundingClientRect();
      Object.entries(prevStyles).forEach(([attr, val]) => {
        node.style[attr as "position"] = val;
      });
      const restTransform = getRestTransform();

      // could be simplified now that we have access to startPx?
      node.style.transform = "";
      const noTransformRect = node.getBoundingClientRect();

      node.style.transform = restTransform;
      const postReleaseRect = node.getBoundingClientRect();

      const diff = preReleaseRect.top - postReleaseRect.top;
      const y = postReleaseRect.top - noTransformRect.top + diff;
      const x = 0;
      // console.log({
      //   pre: preReleaseRect.top,
      //   post: postReleaseRect.top,
      //   noTransformRect: noTransformRect.top,
      // });
      node.style.transform = `translate(${x}px, ${y}px)`;
      const timeoutId = setTimeout(() => {
        node.style.transition = "transform 0.2s ease-in-out";
        node.style.transform = restTransform;
        useDragStore.getState().setDropAnimation(null);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            node.style.transition = "";
          });
        });
      });
      useDragStore
        .getState()
        .setDropAnimation({key, from: {x: preReleaseRect.left, y: preReleaseRect.top}, timeoutId});
    },
    setNextPosition: (pos) => {
      lastPos = pos;
      const y = nodeRect.top + pos.y - startPos.y;
      const x = nodeRect.left + pos.x - startPos.x;
      node.style.transform = `translate(${x}px, ${y}px)`;
    },
    // a re-render during the drag may overwrite the transform, e.g. when startPx changes
    reapply: () => {
      const y = nodeRect.top + lastPos.y - startPos.y;
      const x = nodeRect.left + lastPos.x - startPos.x;
      node.style.transform = `translate(${x}px, ${y}px)`;
    },
  };
};

const setupHandlers = (opts: {setIsDragged: Dispatch<boolean>; optsRef: RefObject<DragOpts>}) => {
  const {optsRef, setIsDragged} = opts;
  let activeMoveStyleManager: MoveStyleManager | null = null;
  const dragHandlers = setupDragHandlers((startPos) => {
    const node = optsRef.current.nodeRef.current;
    if (!node) return null;
    const nodeRect = node.getBoundingClientRect();
    const moveStyleManager = createMoveStyleManager({
      node,
      nodeRect,
      startPos,
      key: optsRef.current.key,
      getRestTransform: () => `translateY(${optsRef.current.startPx}px)`,
    });
    activeMoveStyleManager = moveStyleManager;
    useDragStore.getState().setDragInfo({
      key: optsRef.current.key,
      pos: startPos,
      startIndex: optsRef.current.index,
      currentIndex: optsRef.current.index,
      gapRect: nodeRect,
      onDrop: null,
    });
    const dir = optsRef.current.direction || "all";
    const toPos = (input: Point) => {
      switch (dir) {
        case "vertical":
          return {x: startPos.x, y: input.y};
        case "all":
          return input;
      }
    };
    return {
      onMove: (rawPos) => {
        const pos = toPos(rawPos);
        moveStyleManager.setNextPosition(pos);
        useDragStore.getState().updateDragInfo({pos, startIndex: optsRef.current.index});
      },
      onStop: (isCancelled) => {
        const targetPos = startPos;
        if (!isCancelled) {
          // the list may have changed since the last move, so refresh startIndex before dropping
          useDragStore.getState().updateDragInfo({startIndex: optsRef.current.index});
          const {dragInfo} = useDragStore.getState();
          dragInfo?.onDrop?.();
        }
        activeMoveStyleManager = null;
        moveStyleManager.resetTo(targetPos);
      },
      onUnmount: () => {
        useDragStore.getState().setDragInfo(null);
        setIsDragged(false);
      },
    };
  });
  return {...dragHandlers, reapplyDragPosition: () => activeMoveStyleManager?.reapply()};
};

export const useDragItemKey = () => useDragStore((s) => s.dragInfo?.key ?? null);

type Direction = "vertical" | "all";

type DragOpts = {
  key: Key;
  index: number;
  direction?: Direction;
  nodeRef: RefObject<HTMLElement | null>;
  startPx: number;
  onDragged?: Dispatch<boolean>;
};

export const useDrag = (opts: DragOpts) => {
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });
  const [{handlers, cancel, reapplyDragPosition}] = useState(() =>
    setupHandlers({setIsDragged: (val) => optsRef.current.onDragged?.(val), optsRef})
  );
  // Cancelling (rather than just unsubscribing) restores the node's styles. StrictMode in dev runs
  // this cleanup for moved list items, so a list change mid-drag cancels the drag there.
  useEffect(() => cancel, [cancel]);
  useLayoutEffect(() => {
    reapplyDragPosition();
    const {dropAnimation, setDropAnimation} = useDragStore.getState();
    if (!dropAnimation) return;
    if (opts.key === dropAnimation.key) {
      const node = opts.nodeRef.current;
      if (!node) return;
      clearTimeout(dropAnimation.timeoutId);
      setDropAnimation(null);
      node.style.transform = `translateY(${optsRef.current.startPx}px)`;
      const postReleaseRect = node.getBoundingClientRect();
      node.style.transform = "";
      // could be simplified now that we have access to startPx?
      const noTransformRect = node.getBoundingClientRect();
      const diff = dropAnimation.from.y - postReleaseRect.top;
      const y = postReleaseRect.top - noTransformRect.top + diff;
      const x = 0;
      node.style.transform = `translate(${x}px, ${y}px)`;
      setTimeout(() => {
        node.style.transition = "transform 0.2s ease-in-out";
        node.style.transform = `translateY(${optsRef.current.startPx}px)`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            node.style.transition = "";
          });
        });
      });
    }
  });

  return {handlers};
};

const isWithin = (
  pos: Point,
  rect: {left: number; right: number; top: number; bottom: number}
): boolean => {
  return pos.x >= rect.left && pos.x <= rect.right && pos.y >= rect.top && pos.y <= rect.bottom;
};

type ScrollManager = {
  onUpdatePos: (pos: Point, rect: DOMRect | null) => void;
  unsubscribe: () => void;
};

const createScrollManager = ({
  el,
  onScroll,
}: {
  el: HTMLElement;
  onScroll: () => void;
}): ScrollManager => {
  let momentum = 0;
  let intensity = 0;
  let nextRaf: null | number = null;

  const handleNextFrame = () => {
    nextRaf = requestAnimationFrame(() => {
      const currSpeed = Math.abs(momentum);
      const power = 1 + (Math.abs(intensity) - 3) / 50;
      const nextSpeed = Math.min(50, Math.max(2, currSpeed ** power));
      momentum = intensity < 0 ? -nextSpeed : nextSpeed;
      el.scrollTop += momentum;
      onScroll();
      handleNextFrame();
    });
  };

  const cancel = () => {
    if (nextRaf) {
      cancelAnimationFrame(nextRaf);
      nextRaf = null;
    }
  };

  const ensureScroll = (nextIntensity: number) => {
    intensity = nextIntensity;
    if (!nextRaf) handleNextFrame();
  };

  return {
    onUpdatePos: (pos, rect) => {
      if (!rect) {
        cancel();
        return;
      }
      const scrollRectHeight = Math.min(
        100,
        Math.max(rect.height * 0.2, Math.min(rect.height * 0.5, 15))
      );
      const topRect = {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.top + scrollRectHeight,
      };
      const bottomRect = {
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        top: rect.bottom - scrollRectHeight,
      };
      if (isWithin(pos, topRect)) {
        const canScrollUp = el.scrollTop > 0;
        if (canScrollUp) {
          const relDistanceToTop = (pos.y - topRect.top) / scrollRectHeight;
          ensureScroll(-Math.max(1, Math.ceil((1 - relDistanceToTop) * 6)));
          return;
        }
      }
      if (isWithin(pos, bottomRect)) {
        const relDistanceToBottom = (bottomRect.bottom - pos.y) / scrollRectHeight;
        const canScrollDown = el.clientHeight + el.scrollTop < el.scrollHeight;
        if (canScrollDown) {
          ensureScroll(Math.max(1, Math.ceil((1 - relDistanceToBottom) * 6)));
          return;
        }
      }
      cancel();
    },
    unsubscribe: cancel,
  };
};

type DropZoneManager = {
  unsubscribe: () => void;
};

const createDropZoneManager = (
  optsRef: RefObject<DropOpts>,
  dragInfo: DragInfo
): DropZoneManager => {
  const unsubs: (() => void)[] = [];
  const el = optsRef.current.getElement();

  let prevPos = dragInfo.pos;
  let prevIdx: null | number = null;

  const handleTargetIndex = (pos: Point | null) => {
    const idx = pos ? optsRef.current.getTargetIdx({pos, node: el}) : null;
    if (prevIdx === idx) return;
    const handleDrop =
      idx === null
        ? null
        : () => {
            const {onDrop} = optsRef.current;
            if (!onDrop) return null;
            // read startIndex at drop time: dragInfo is from drag start and goes stale if the list changes
            const startIndex = useDragStore.getState().dragInfo?.startIndex ?? dragInfo.startIndex;
            onDrop({key: dragInfo.key, startIndex, targetIndex: idx});
          };
    useDragStore.getState().updateDragInfo({currentIndex: idx, onDrop: handleDrop});
    prevIdx = idx;
  };

  const scrollManager = createScrollManager({el, onScroll: () => handleTargetIndex(prevPos)});
  unsubs.push(scrollManager.unsubscribe);

  let prevMode: DropMode = null;
  const rect = el.getBoundingClientRect();

  const setMode = (mode: DropMode) => {
    if (!optsRef.current.onModeChange) return;
    if (prevMode === mode) return;
    optsRef.current.onModeChange(mode, dragInfo.key, dragInfo.startIndex);
    prevMode = mode;
  };

  unsubs.push(() => setMode(null));

  const handlePosChange = (pos: Point | null) => {
    if (!pos) return;
    prevPos = pos;
    const isInside = isWithin(pos, rect);
    setMode(isInside ? "isOver" : "canDrop");
    if (isInside) {
      handleTargetIndex(pos);
      scrollManager.onUpdatePos(pos, rect);
    } else {
      handleTargetIndex(null);
      scrollManager.onUpdatePos(pos, null);
    }
  };

  handlePosChange(dragInfo.pos);
  unsubs.push(useDragStore.subscribe((s) => s.dragInfo?.pos ?? null, handlePosChange));

  return {
    unsubscribe: () => unsubs.forEach((fn) => fn()),
  };
};

type DropMode = null | "canDrop" | "isOver";
type DropOpts = {
  getElement: () => HTMLElement;
  onModeChange?: (mode: DropMode, key: Key | null, index: number | null) => void;
  getTargetIdx: (opts: {pos: Point; node: HTMLElement}) => number | null;
  onDrop?: (opts: {key: Key; startIndex: number; targetIndex: number}) => void;
};

export const useDrop = (opts: DropOpts) => {
  const optsRef = useRef<DropOpts>(opts);
  useEffect(() => {
    optsRef.current = opts;
  });
  const dropManagerRef = useRef<DropZoneManager | null>(null);

  useEffect(() => {
    const unsub = useDragStore.subscribe(
      (s) => s.dragInfo?.key ?? null,
      (dragKey) => {
        if (dragKey !== null) {
          dropManagerRef.current?.unsubscribe();
          dropManagerRef.current = createDropZoneManager(
            optsRef,
            useDragStore.getState().dragInfo!
          );
        } else {
          dropManagerRef.current?.unsubscribe();
          dropManagerRef.current = null;
        }
      }
    );
    return () => {
      unsub();
      dropManagerRef.current?.unsubscribe();
    };
  }, []);
};
